import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Text,
  Tooltip,
  useColorModeValue,
  VStack,
  HStack,
  Badge,
  Icon,
  Flex
} from '@chakra-ui/react';
import { FaStar, FaBuilding } from 'react-icons/fa';
import { useUser } from '../contexts/UserContext';

/**
 * BusinessBubbleChart - Displays businesses as interactive bubbles
 * 
 * Each bubble's size is proportional to the provided metric value.
 * Bubbles are clickable and show tooltips with business details.
 * 
 * @param {Array} businesses - Array of business objects
 * @param {string} valueKey - Key in business object to use for bubble size (e.g., 'rating_count')
 * @param {string} valueLabel - Label for the metric (e.g., 'Total Reviews')
 * @param {function} onBusinessClick - Callback when a bubble is clicked
 * @param {function} formatValue - Optional function to format the value display
 * @param {string} colorScheme - Color scheme for bubbles ('blue', 'green', 'purple', 'orange')
 */
const BusinessBubbleChart = ({
  businesses = [],
  valueKey = 'rating_count',
  valueLabel = 'Reviews',
  onBusinessClick,
  formatValue = (val) => val?.toLocaleString() || '0',
  colorScheme = 'blue'
}) => {
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [hoveredBubble, setHoveredBubble] = useState(null);
  
  const { favoriteUris } = useUser();
  
  // Color mode values
  const bgColor = useColorModeValue('gray.50', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const tooltipBg = useColorModeValue('white', 'gray.700');
  
  // Distinct color palette for individual bubbles - vibrant and varied
  const bubbleColorPalette = [
    '#E53E3E', // Red
    '#DD6B20', // Orange
    '#D69E2E', // Yellow
    '#38A169', // Green
    '#319795', // Teal
    '#3182CE', // Blue
    '#805AD5', // Purple
    '#D53F8C', // Pink
    '#00B5D8', // Cyan
    '#ED8936', // Light Orange
    '#48BB78', // Light Green
    '#4299E1', // Light Blue
    '#9F7AEA', // Light Purple
    '#F56565', // Light Red
    '#68D391', // Mint
    '#63B3ED', // Sky Blue
    '#B794F4', // Lavender
    '#FC8181', // Salmon
    '#F6AD55', // Peach
    '#4FD1C5', // Aqua
    '#76E4F7', // Light Cyan
    '#FBD38D', // Gold
    '#9AE6B4', // Pale Green
    '#90CDF4', // Pale Blue
    '#D6BCFA', // Pale Purple
  ];
  
  // Color schemes for legend display
  const colorSchemes = {
    blue: {
      gradient: ['#3182CE', '#2B6CB0', '#2C5282'],
      light: '#EBF8FF',
      border: '#90CDF4',
      text: '#2B6CB0'
    },
    green: {
      gradient: ['#38A169', '#2F855A', '#276749'],
      light: '#F0FFF4',
      border: '#9AE6B4',
      text: '#276749'
    },
    purple: {
      gradient: ['#805AD5', '#6B46C1', '#553C9A'],
      light: '#FAF5FF',
      border: '#D6BCFA',
      text: '#553C9A'
    },
    orange: {
      gradient: ['#DD6B20', '#C05621', '#9C4221'],
      light: '#FFFAF0',
      border: '#FBD38D',
      text: '#9C4221'
    },
    teal: {
      gradient: ['#319795', '#2C7A7B', '#285E61'],
      light: '#E6FFFA',
      border: '#81E6D9',
      text: '#285E61'
    }
  };
  
  const colors = colorSchemes[colorScheme] || colorSchemes.blue;
  
  // Update container size on mount and resize using ResizeObserver
  useEffect(() => {
    let mounted = true;
    
    const updateSize = () => {
      if (!mounted || !containerRef.current) return;
      
      const rect = containerRef.current.getBoundingClientRect();
      // Only update if we have valid dimensions
      if (rect.width > 0 && rect.height > 0) {
        setContainerSize(prev => {
          // Only update if changed to avoid unnecessary re-renders
          if (prev.width !== rect.width || prev.height !== rect.height) {
            return { width: rect.width, height: rect.height };
          }
          return prev;
        });
      }
    };

    // Initial size check
    updateSize();
    
    // Use ResizeObserver for more reliable size detection
    let resizeObserver;
    if (containerRef.current && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const { width, height } = entry.contentRect;
          if (width > 0 && height > 0 && mounted) {
            setContainerSize(prev => {
              if (prev.width !== width || prev.height !== height) {
                return { width, height };
              }
              return prev;
            });
          }
        }
      });
      resizeObserver.observe(containerRef.current);
    }

    // Fallback: multiple delayed checks for initial render and lazy tabs
    const timers = [
      setTimeout(updateSize, 50),
      setTimeout(updateSize, 100),
      setTimeout(updateSize, 200),
      setTimeout(updateSize, 400),
      setTimeout(updateSize, 800)
    ];
    
    // Extra fallback: interval check for first 2 seconds (for lazy-loaded tabs)
    let intervalCheck = setInterval(() => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          updateSize();
          clearInterval(intervalCheck);
        }
      }
    }, 100);
    
    // Clear interval after 2 seconds regardless
    const clearIntervalTimer = setTimeout(() => {
      clearInterval(intervalCheck);
    }, 2000);
    
    window.addEventListener('resize', updateSize);
    
    return () => {
      mounted = false;
      window.removeEventListener('resize', updateSize);
      timers.forEach(t => clearTimeout(t));
      clearTimeout(clearIntervalTimer);
      clearInterval(intervalCheck);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [businesses.length]);

  // Calculate bubble positions using a pack layout algorithm
  const bubbleData = useMemo(() => {
    if (!businesses.length || containerSize.width === 0) return [];
    
    const { width, height } = containerSize;
    const numBusinesses = businesses.length;
    
    // Get values and calculate size range
    const values = businesses.map(b => Math.max(1, b[valueKey] || 0));
    const maxValue = Math.max(...values);
    const minValue = Math.min(...values);
    
    // Calculate available area (leaving margin for UI elements)
    const availableWidth = width - 20;
    const availableHeight = height - 80;
    const totalAvailableArea = availableWidth * availableHeight * 0.70;
    
    // For proper visual correlation: bubble AREA should be proportional to VALUE
    // Area = π * r², so r = sqrt(Area / π)
    // If we want Area ∝ Value, then r ∝ sqrt(Value)
    
    // Calculate sum of all values (area will be proportional to value)
    const totalValue = values.reduce((sum, v) => sum + v, 0);
    
    // Calculate the scaling factor to fit all bubbles in available area
    // Total area of all bubbles = sum of (value_i / totalValue * totalAvailableArea)
    // We need to find what radius the max value should have
    const maxBubbleArea = (maxValue / totalValue) * totalAvailableArea;
    const theoreticalMaxRadius = Math.sqrt(maxBubbleArea / Math.PI);
    
    // Set practical bounds based on container
    const absoluteMaxRadius = Math.min(width, height) * 0.25;
    const absoluteMinRadius = 18; // Minimum readable size
    
    // Scale factor to fit within bounds
    const scaleFactor = Math.min(1, absoluteMaxRadius / theoreticalMaxRadius);
    
    // Create bubble objects with properly proportional radii
    const bubbles = businesses.map((business, index) => {
      const value = Math.max(1, business[valueKey] || 0);
      
      // Area proportional to value
      const bubbleArea = (value / totalValue) * totalAvailableArea * scaleFactor * scaleFactor;
      let radius = Math.sqrt(bubbleArea / Math.PI);
      
      // Apply minimum radius for readability, but keep proportions for larger values
      radius = Math.max(absoluteMinRadius, radius);
      
      return {
        ...business,
        value,
        radius,
        index,
        // Initial position - spread across container
        x: width / 2 + (Math.random() - 0.5) * width * 0.6,
        y: height / 2 + (Math.random() - 0.5) * height * 0.4
      };
    });
    
    // Sort by value descending for rendering order (larger bubbles in back)
    bubbles.sort((a, b) => b.value - a.value);
    
    // Force-directed layout simulation with tighter packing
    const iterations = 150;
    const centerX = width / 2;
    const centerY = height / 2;
    
    for (let i = 0; i < iterations; i++) {
      const alpha = 1 - i / iterations;
      
      for (let j = 0; j < bubbles.length; j++) {
        const bubble = bubbles[j];
        
        // Stronger center gravity for tighter packing
        const dx = centerX - bubble.x;
        const dy = centerY - bubble.y;
        bubble.x += dx * 0.04 * alpha;
        bubble.y += dy * 0.04 * alpha;
        
        // Collision detection with minimal padding
        for (let k = j + 1; k < bubbles.length; k++) {
          const other = bubbles[k];
          const ddx = bubble.x - other.x;
          const ddy = bubble.y - other.y;
          const dist = Math.sqrt(ddx * ddx + ddy * ddy);
          const minDist = bubble.radius + other.radius + 2;
          
          if (dist < minDist && dist > 0) {
            const force = (minDist - dist) / dist * 0.5;
            const fx = ddx * force;
            const fy = ddy * force;
            
            bubble.x += fx;
            bubble.y += fy;
            other.x -= fx;
            other.y -= fy;
          }
        }
        
        // Boundary constraints with minimal padding
        const padding = 4;
        bubble.x = Math.max(bubble.radius + padding, Math.min(width - bubble.radius - padding, bubble.x));
        bubble.y = Math.max(bubble.radius + padding, Math.min(height - bubble.radius - padding, bubble.y));
      }
    }
    
    return bubbles;
  }, [businesses, valueKey, containerSize]);

  // Handle bubble click
  const handleBubbleClick = useCallback((business) => {
    if (onBusinessClick) {
      onBusinessClick(business);
    }
  }, [onBusinessClick]);

  // Check if business is favorited
  const isFavorited = useCallback((business) => {
    return business.maps_uri && favoriteUris?.includes(business.maps_uri);
  }, [favoriteUris]);

  // Get short name for display
  const getShortName = (name) => {
    if (!name) return '';
    const words = name.split(' ');
    if (words.length <= 2) return name;
    return words.slice(0, 2).join(' ');
  };

  // Get bubble color - each bubble gets a unique color from the palette
  const getBubbleColor = (bubble, index) => {
    // Cycle through the color palette for each bubble
    return bubbleColorPalette[index % bubbleColorPalette.length];
  };

  if (!businesses.length) {
    return (
      <Box
        ref={containerRef}
        w="100%"
        h="100%"
        minH="500px"
        bg={bgColor}
        borderRadius="lg"
        borderWidth="1px"
        borderColor={borderColor}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Text color="gray.500">No businesses to display</Text>
      </Box>
    );
  }

  // Show loading state while container size is being determined
  const isLoading = containerSize.width === 0 || containerSize.height === 0;

  return (
    <Box
      ref={containerRef}
      position="relative"
      w="100%"
      h="100%"
      minH="500px"
      bg={bgColor}
      borderRadius="lg"
      borderWidth="1px"
      borderColor={borderColor}
      overflow="hidden"
      sx={{ containerType: 'size' }}
    >
      {/* Legend */}
      {!isLoading && (
        <Box
          position="absolute"
          top={4}
          left={4}
          zIndex={10}
          bg={tooltipBg}
          p={3}
          borderRadius="md"
          shadow="md"
          borderWidth="1px"
          borderColor={borderColor}
        >
          <Text fontSize="xs" fontWeight="bold" color={textColor} mb={2}>
            Bubble Size = {valueLabel}
          </Text>
          <HStack spacing={1}>
            {/* Show a sample of colors from the palette */}
            {bubbleColorPalette.slice(0, 8).map((color, i) => (
              <Box 
                key={i} 
                w="10px" 
                h="10px" 
                borderRadius="full" 
                bg={color} 
              />
            ))}
            <Text fontSize="xs" color="gray.500" ml={1}>Each business</Text>
          </HStack>
        </Box>
      )}

      {/* Stats summary */}
      {!isLoading && (
        <Box
          position="absolute"
          top={4}
          right={4}
          zIndex={10}
          bg={tooltipBg}
          p={3}
          borderRadius="md"
          shadow="md"
          borderWidth="1px"
          borderColor={borderColor}
        >
          <VStack align="end" spacing={1}>
            <Text fontSize="xs" color="gray.500">
              {businesses.length} businesses
            </Text>
            <Text fontSize="sm" fontWeight="bold" color={colors.text}>
              Total: {formatValue(businesses.reduce((sum, b) => sum + (b[valueKey] || 0), 0))}
            </Text>
          </VStack>
        </Box>
      )}

      {/* Loading state */}
      {isLoading && (
        <Flex
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          direction="column"
          align="center"
          gap={2}
        >
          <Text color="gray.500" fontSize="sm">Loading bubble visualization...</Text>
          <Text color="gray.400" fontSize="xs">{businesses.length} businesses</Text>
        </Flex>
      )}

      {/* Bubbles */}
      {!isLoading && bubbleData.map((bubble, index) => {
        const bubbleId = bubble.maps_uri || bubble.name || index;
        const isHovered = hoveredBubble === bubbleId;
        const isFav = isFavorited(bubble);
        const bubbleColor = getBubbleColor(bubble, index);
        
        return (
          <Tooltip
            key={bubbleId}
            label={
              <VStack align="start" spacing={1} p={1}>
                <Text fontWeight="bold" fontSize="sm">{bubble.name}</Text>
                <HStack spacing={2}>
                  <Badge colorScheme={colorScheme}>{valueLabel}</Badge>
                  <Text fontWeight="bold">{formatValue(bubble.value)}</Text>
                </HStack>
                {bubble.rating_avg && (
                  <HStack spacing={1}>
                    <Icon as={FaStar} color="yellow.400" boxSize={3} />
                    <Text fontSize="sm">{bubble.rating_avg.toFixed(1)}</Text>
                  </HStack>
                )}
                {bubble.address && (
                  <Text fontSize="xs" color="gray.400" noOfLines={1}>
                    {bubble.address}
                  </Text>
                )}
                <Text fontSize="xs" color="gray.400" mt={1}>
                  Click to view details
                </Text>
              </VStack>
            }
            placement="top"
            hasArrow
            bg={tooltipBg}
            color={textColor}
            borderRadius="md"
            shadow="lg"
          >
            <Flex
              position="absolute"
              left={`${bubble.x}px`}
              top={`${bubble.y}px`}
              transform="translate(-50%, -50%)"
              w={`${bubble.radius * 2}px`}
              h={`${bubble.radius * 2}px`}
              borderRadius="full"
              bg={bubbleColor}
              cursor="pointer"
              alignItems="center"
              justifyContent="center"
              flexDirection="column"
              onClick={() => handleBubbleClick(bubble)}
              onMouseEnter={() => setHoveredBubble(bubbleId)}
              onMouseLeave={() => setHoveredBubble(null)}
              transition="all 0.2s ease-out"
              _hover={{
                transform: 'translate(-50%, -50%) scale(1.1)',
                shadow: 'xl',
                zIndex: 100
              }}
              shadow={isHovered ? 'xl' : 'md'}
              zIndex={isHovered ? 100 : bubbleData.length - index}
              borderWidth={isFav ? '3px' : '0'}
              borderColor={isFav ? 'yellow.400' : 'transparent'}
              overflow="hidden"
            >
              {/* Bubble content */}
              <VStack spacing={0} p={1}>
                {bubble.radius >= 40 && (
                  <Text
                    fontSize={bubble.radius >= 60 ? 'xs' : '2xs'}
                    fontWeight="bold"
                    color="white"
                    textAlign="center"
                    noOfLines={bubble.radius >= 60 ? 2 : 1}
                    lineHeight="1.2"
                    px={1}
                  >
                    {getShortName(bubble.name)}
                  </Text>
                )}
                <Text
                  fontSize={bubble.radius >= 50 ? 'sm' : 'xs'}
                  fontWeight="bold"
                  color="whiteAlpha.900"
                >
                  {formatValue(bubble.value)}
                </Text>
              </VStack>
              
              {/* Favorite star indicator */}
              {isFav && (
                <Box
                  position="absolute"
                  top={-1}
                  right={-1}
                  bg="yellow.400"
                  borderRadius="full"
                  p={1}
                >
                  <Icon as={FaStar} color="white" boxSize={2} />
                </Box>
              )}
              
              {/* Shine effect */}
              <Box
                position="absolute"
                top="10%"
                left="15%"
                w="30%"
                h="20%"
                borderRadius="full"
                bg="whiteAlpha.300"
                transform="rotate(-30deg)"
              />
            </Flex>
          </Tooltip>
        );
      })}

      {/* Instructions */}
      {!isLoading && (
        <Box
          position="absolute"
          bottom={4}
          left="50%"
          transform="translateX(-50%)"
          bg={tooltipBg}
          px={4}
          py={2}
          borderRadius="full"
          shadow="sm"
          borderWidth="1px"
          borderColor={borderColor}
        >
          <HStack spacing={2}>
            <Icon as={FaBuilding} color="gray.400" boxSize={3} />
            <Text fontSize="xs" color="gray.500">
              Hover over bubbles to see details • Click to open business profile
            </Text>
          </HStack>
        </Box>
      )}
    </Box>
  );
};

export default BusinessBubbleChart;
